/* eslint-disable no-undef */

/**
 * Very efficient function with debounce and throttle events. Attaches both throttle and debounce listeners to a given {@link target} element and {@link eventListener}.
 * @param {HTMLElement} target The target element to attach the event listener to
 * @param {keyof HTMLElementEventMap} eventListener The event listener to be attached to the {@link target} element.
 * @param {() => void} callbackFunction The function to be called when the event listener is triggered.
 * @param {number} repeatAfter The time, in milliseconds, to wait until the next execution.
 * @param {boolean=} callOnLoad Call {@link callbackFunction} when this function is called
 * @returns {void}
 * @example
 *   // Add a debounce and throttle listener to the window scroll event, with a wait of 100ms between each execution:
 *   myEfficientFn(window, 'scroll', () => console.log('Hello World'), 100);
 */
export function myEfficientFn(target, eventListener, callbackFunction, repeatAfter, callOnLoad = false) {
  const throttle = (f, w) => {
    let t;

    return (...args) => {
      if (!t) {
        f(...args);
        t = true;
        setTimeout(() => (t = false), w);
      }
    };
  };

  const debounce = (f, w, i) => {
    let t;

    return (...args) => {
      const later = () => {
        t = null;
        if (!i) f(...args);
      };

      const shouldCallNow = i && !t;
      clearTimeout(t);
      t = setTimeout(later, w);
      if (shouldCallNow) f(...args);
    };
  };

  if (callOnLoad) callbackFunction();

  target.addEventListener(eventListener, throttle(() => callbackFunction(), repeatAfter));
  target.addEventListener(eventListener, debounce(() => callbackFunction(), repeatAfter));
}

/**
 * The browser decides which file to load from a `srcset`. When selecting the image size, the browser is normally based on the size of the browser window. Instead, we recommend that the browser uses the size in which the image is actually displayed.
 */
export const handleImageSizes = () => {
  const items = Array.from(
    document.querySelectorAll('img[sizes]')
  );

  myEfficientFn(window, 'resize', () => {
    items.forEach((image) => {
      /** The size at which the {@link image} is actually displayed. In pixel. */
      const imageWidth = Math.round(image.getBoundingClientRect().width);
      image.sizes = imageWidth > 0 ? imageWidth + 'px' : 'auto';
    });
  }, 100, true);
};
